import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { axiosClient } from "@/lib/axiosClient";

// =========================
// Types
// =========================

export interface ConferenceOutputAuthor {
    id?: number;
    output_registration_id?: number;
    seq: number;
    given_name: string;
    family_name?: string | null;
    affiliation?: string | null;
    email: string;
    orcid?: string | null;
    is_corresponding: boolean;
}

export interface ConferenceOutputConference {
    conference_id: number;
    name: string;
    slug: string;
}

export interface ConferenceOutputRegistrationSummary {
    registration_id: number;
    ojs_submission_id?: string | null;
    submission_title?: string | null;
    submission_authors?: string | null;
    participant_name?: string | null;
    email?: string | null;
    status?: string | null;
    nim?: string | null;
    nik?: string | null;
    participant_type?: string | null;
    is_presenter?: boolean | number | null;
    institution_name?: string | null;
}

export interface MasterJournal {
    id: number;
    journal_name: string;
    journal_url?: string | null;
    editor_in_chief?: string | null;
    quartile?: string | null;
    sinta_rank?: string | null;
    lptk_institution?: string | null;
    scope_description?: string | null;
}

export interface ConferenceOutputDecision {
    id: number;
    registration_id: number;
    conference_id: number | null;
    output_route: "journal" | "proceeding";
    publisher: string | null;
    scopus_journal_id: number | null;
    sinta_journal_id: number | null;
    journal_admin_note: string | null;
    author_decision: "pending" | "agreed" | "declined";
    author_decision_at: string | null;
    decision_token: string | null;
    decision_email_sent_at: string | null;
    decision_deadline: string | null;
    proceeding_submission_deadline: string | null;
    submission_deadline_extended_at: string | null;
    imported_from_batch_id: number | null;
    created_at: string;
    updated_at: string;
    registration?: ConferenceOutputRegistrationSummary;
    conference?: ConferenceOutputConference;
    // Catatan: Laravel men-serialize nama relasi eager-load ke snake_case saat toArray()/toJson()
    // (scopusJournal() -> "scopus_journal" di JSON), bukan tetap camelCase seperti nama method PHP-nya.
    scopus_journal?: MasterJournal | null;
    sinta_journal?: MasterJournal | null;
    output_registration?: ConferenceOutputRegistration | null;
    import_batch?: { id: number; file_name: string } | null;
}

export interface ConferenceOutputBilling {
    id: number;
    output_registration_id: number;
    fee_id: number | null;
    amount: string;
    currency: "IDR" | "USD";
    invoice_number: string;
    customer_number: string | null;
    gateway_transaction_id: string | null;
    status: "unpaid" | "paid" | "expired" | "cancelled";
    created_at_payment: string | null;
    expired_at_payment: string | null;
    valid_at: string | null;
    receipt_sent_at: string | null;
    extension_count: number;
    last_extended_at: string | null;
    extend_reason: string | null;
    fee?: { publisher: string; price: string; currency: string } | null;
    registration?: ConferenceOutputRegistration;
    created_at: string;
    updated_at?: string;
}

export interface ConferenceOutputRegistration {
    id: number;
    decision_id: number;
    paper_title_final: string | null;
    abstract_final: string | null;
    conference_proceedings_collection: string | null;
    ojs_metadata_fetched_at: string | null;
    camera_ready_file_path: string | null;
    camera_ready_uploaded_at: string | null;
    copyright_form_file_path: string | null;
    copyright_form_uploaded_at: string | null;
    checklist_originality_similarity_below_20: boolean;
    checklist_metadata_matches_publisher: boolean;
    checklist_used_template: boolean;
    checklist_fee_commitment: boolean;
    status: "draft" | "submitted" | "payment_pending" | "paid" | "expired" | "cancelled";
    public_token: string;
    submitted_at: string | null;
    authors?: ConferenceOutputAuthor[];
    decision?: ConferenceOutputDecision;
    billing?: ConferenceOutputBilling | null;
    publisher_info?: ConferenceOutputPublisherInfo | null;
    fee_info?: { amount: string; currency: string } | null;
}

export interface ConferenceOutputPublisherInfo {
    id: number;
    code: string;
    display_name: string;
    website_url: string | null;
    template_file_path: string | null;
    guideline_file_path: string | null;
    is_active: boolean;
}

interface ApiEnvelope<T> {
    status: string;
    message?: string;
    data: T;
}

// =========================
// Public - Decision (agree journal / decline to proceeding)
// =========================

export const useConferenceOutputDecisionByToken = (token?: string) => {
    return useQuery({
        queryKey: ["conference-output-decision-token", token],
        queryFn: async () => {
            const { data } = await axiosClient.get<ApiEnvelope<ConferenceOutputDecision>>(
                `/conference-output/decision/token/${encodeURIComponent(token || "")}`
            );
            return data.data;
        },
        enabled: !!token,
        retry: 1,
    });
};

export const useAgreeJournalDecision = (token?: string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async () => {
            const { data } = await axiosClient.post(`/conference-output/decision/token/${encodeURIComponent(token || "")}/agree-journal`);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["conference-output-decision-token", token] });
        },
    });
};

export const useDeclineToProceedingDecision = (token?: string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async () => {
            const { data } = await axiosClient.post(`/conference-output/decision/token/${encodeURIComponent(token || "")}/decline-to-proceeding`);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["conference-output-decision-token", token] });
        },
    });
};

// =========================
// Public - Proceeding Registration (metadata, uploads, checklist, submit)
// =========================

export const useConferenceOutputRegistrationByToken = (token?: string) => {
    return useQuery({
        queryKey: ["conference-output-registration-token", token],
        queryFn: async () => {
            const { data } = await axiosClient.get<ApiEnvelope<ConferenceOutputRegistration>>(
                `/conference-output/registration/token/${encodeURIComponent(token || "")}`
            );
            return data.data;
        },
        enabled: !!token,
        retry: 1,
    });
};

export const useRefetchOutputMetadata = (token?: string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (apply: boolean) => {
            const { data } = await axiosClient.post(
                `/conference-output/registration/token/${encodeURIComponent(token || "")}/refetch-metadata`,
                {},
                { params: { apply: apply ? "true" : "false" } }
            );
            return data;
        },
        onSuccess: (_data, apply) => {
            if (apply) {
                queryClient.invalidateQueries({ queryKey: ["conference-output-registration-token", token] });
            }
        },
    });
};

export interface UpdateOutputRegistrationPayload {
    paper_title_final?: string;
    abstract_final?: string | null;
    conference_proceedings_collection?: string | null;
    checklist_originality_similarity_below_20?: boolean;
    checklist_metadata_matches_publisher?: boolean;
    checklist_used_template?: boolean;
    checklist_fee_commitment?: boolean;
    authors?: Omit<ConferenceOutputAuthor, "id" | "output_registration_id">[];
}

export const useUpdateOutputRegistration = (token?: string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (payload: UpdateOutputRegistrationPayload) => {
            const { data } = await axiosClient.put(`/conference-output/registration/token/${encodeURIComponent(token || "")}`, payload);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["conference-output-registration-token", token] });
        },
    });
};

export const useUploadOutputCameraReady = (token?: string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (file: File) => {
            const formData = new FormData();
            formData.append("file", file);
            const { data } = await axiosClient.post(
                `/conference-output/registration/token/${encodeURIComponent(token || "")}/upload-camera-ready`,
                formData,
                { headers: { "Content-Type": "multipart/form-data" } }
            );
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["conference-output-registration-token", token] });
        },
    });
};

export const useUploadOutputCopyright = (token?: string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (file: File) => {
            const formData = new FormData();
            formData.append("file", file);
            const { data } = await axiosClient.post(
                `/conference-output/registration/token/${encodeURIComponent(token || "")}/upload-copyright`,
                formData,
                { headers: { "Content-Type": "multipart/form-data" } }
            );
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["conference-output-registration-token", token] });
        },
    });
};

export const getOutputCopyrightFormPdfUrl = (token: string, baseURL: string) =>
    `${baseURL}/conference-output/registration/token/${encodeURIComponent(token)}/copyright-form.pdf`;

export const useSubmitOutputRegistration = (token?: string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async () => {
            const { data } = await axiosClient.post(`/conference-output/registration/token/${encodeURIComponent(token || "")}/submit`);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["conference-output-registration-token", token] });
        },
    });
};

// =========================
// Public - Billing (invoice + status)
// =========================

export const useConferenceOutputBillingByToken = (token?: string) => {
    return useQuery({
        queryKey: ["conference-output-billing-token", token],
        queryFn: async () => {
            const { data } = await axiosClient.get<ApiEnvelope<ConferenceOutputBilling>>(
                `/conference-output/billing/token/${encodeURIComponent(token || "")}`
            );
            return data.data;
        },
        enabled: !!token,
        retry: 1,
        refetchInterval: (query) => {
            const data = query.state.data as ConferenceOutputBilling | undefined;
            return data?.status === "unpaid" ? 15000 : false;
        },
    });
};

export const useCheckOutputBillingStatus = (token?: string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async () => {
            const { data } = await axiosClient.post<ApiEnvelope<ConferenceOutputBilling>>(
                `/conference-output/billing/token/${encodeURIComponent(token || "")}/check-status`
            );
            return data.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["conference-output-billing-token", token] });
        },
    });
};
